import React, { createContext, useContext, useCallback, useRef } from "react";

const ScrollManagementContext = createContext();

export const useScrollManagement = () => {
  const context = useContext(ScrollManagementContext);
  if (!context) {
    throw new Error(
      "useScrollManagement must be used within a ScrollManagementProvider"
    );
  }
  return context;
};

export const ScrollManagementProvider = ({ children }) => {
  const scrollableContainerRef = useRef(null);

  // Find the scrollable container (layout's scrollable div)
  const findScrollableContainer = useCallback(() => {
    if (scrollableContainerRef.current) {
      return scrollableContainerRef.current;
    }

    // Look for the layout's scrollable container
    const container =
      document.querySelector('[style*="overflowY: auto"]') ||
      document.querySelector('[style*="overflow-y: auto"]');

    if (container) {
      scrollableContainerRef.current = container;
      return container;
    }

    return null;
  }, []);

  // Scroll to top of the scrollable container
  const scrollToTop = useCallback(() => {
    const container = findScrollableContainer();
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [findScrollableContainer]);

  // Scroll to a specific element within the container
  const scrollToElement = useCallback(
    (element, offset = 0) => {
      const container = findScrollableContainer();
      if (!container || !element) return;

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // Calculate the position relative to the container
      const relativeTop =
        elementRect.top - containerRect.top + container.scrollTop;
      const targetScrollTop = relativeTop - offset;

      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: "smooth",
      });
    },
    [findScrollableContainer]
  );

  // Smart scroll that first scrolls parent to top, then scrolls to element
  const smartScrollToElement = useCallback(
    (element, offset = 0) => {
      const container = findScrollableContainer();
      if (!container || !element) return;

      // Check if we're on mobile (smaller screens need more aggressive scrolling)
      const isMobile = window.innerWidth <= 900;
      const scrollDelay = isMobile ? 150 : 100; // Faster on desktop/tablet
      const scrollOffset = isMobile ? offset : Math.max(offset - 10, 0); // Less offset on larger screens

      // First scroll parent to top to ensure we have scroll space
      container.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      // Then scroll to the specific element after a delay
      setTimeout(() => {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();

        const relativeTop =
          elementRect.top - containerRect.top + container.scrollTop;
        const targetScrollTop = relativeTop - scrollOffset;

        container.scrollTo({
          top: Math.max(0, targetScrollTop),
          behavior: "smooth",
        });
      }, scrollDelay);
    },
    [findScrollableContainer]
  );

  const value = {
    scrollToTop,
    scrollToElement,
    smartScrollToElement,
    findScrollableContainer,
  };

  return (
    <ScrollManagementContext.Provider value={value}>
      {children}
    </ScrollManagementContext.Provider>
  );
};

export default ScrollManagementContext;
